import { useDeferredValue, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ErrorNotice, Layout, StatusPill } from '../components/Layout';
import { api, apiUrl } from '../lib/api';
import type { MeetingNode } from '../lib/types';
import { SearchableTextPanel } from '../components/SearchableTextPanel';

interface Taxonomy {
  id: string;
  name: string;
  color: string;
}
type LegalStatus = 'active' | 'amended' | 'partially_repealed' | 'repealed';
type RelationshipType =
  | 'replaces'
  | 'repeals_all'
  | 'repeals_part'
  | 'amends'
  | 'extends'
  | 'references'
  | 'issued_under';
interface SearchDocument {
  id: string;
  storedFileId: string;
  title: string;
  documentNumber: string | null;
  documentDate: string | null;
  category: Taxonomy | null;
  agency: Taxonomy | null;
  labels: Taxonomy[];
  status: string;
  legalStatus: LegalStatus;
  summaryText: string | null;
  ocrText: string;
  files?: Array<{ id: string; storedFileId: string; displayName: string; ocrText: string; includeInSearch: boolean }>;
  file: { name: string };
  match: null | {
    source: 'metadata' | 'original' | 'attachment';
    fileId?: string;
    fileName: string | null;
    page: number | null;
    snippet: string;
  };
}
interface SearchResponse {
  data: SearchDocument[];
  meta: { total: number; limit: number; offset: number };
}
interface RelationshipEdge {
  id: string;
  source: { id: string; title: string; documentNumber: string | null; documentDate: string | null };
  target: { id: string; title: string; documentNumber: string | null; documentDate: string | null };
  relationshipType: RelationshipType;
  scopeText: string | null;
  effectiveDate: string | null;
  evidenceText: string | null;
  evidencePage: number | null;
  status: 'proposed' | 'confirmed' | 'rejected';
}
interface GraphNode {
  id: string;
  title: string;
  documentNumber: string | null;
  documentDate: string | null;
  legalStatus: LegalStatus;
}
interface GraphResponse {
  rootId: string;
  nodes: GraphNode[];
  edges: RelationshipEdge[];
}

const statusLabels: Record<string, string> = {
  draft: 'รออ่านเอกสาร',
  ocr_queued: 'รอคิว OCR',
  ocr_processing: 'กำลัง OCR',
  needs_review: 'รอตรวจสอบ',
  ready: 'พร้อมใช้',
  ocr_failed: 'OCR ไม่สำเร็จ',
};
const legalLabels: Record<LegalStatus, string> = {
  active: 'ยังมีผลใช้งาน',
  amended: 'ถูกแก้ไขเพิ่มเติม',
  partially_repealed: 'ยกเลิกบางส่วน',
  repealed: 'ยกเลิกทั้งฉบับ',
};
const legalStyles: Record<LegalStatus, string> = {
  active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  amended: 'bg-amber-50 text-amber-800 border-amber-200',
  partially_repealed: 'bg-orange-50 text-orange-800 border-orange-200',
  repealed: 'bg-red-50 text-red-800 border-red-200',
};
const relationshipLabels: Record<RelationshipType, string> = {
  replaces: 'ใช้แทน',
  repeals_all: 'ยกเลิกทั้งฉบับ',
  repeals_part: 'ยกเลิกบางส่วน',
  amends: 'แก้ไขเพิ่มเติม',
  extends: 'ขยายเวลา/ต่ออายุ',
  references: 'อ้างถึง',
  issued_under: 'ออกตาม',
};

function useTaxonomy(kind: 'categories' | 'agencies' | 'labels') {
  return useQuery({
    queryKey: ['document-taxonomy', kind],
    queryFn: () =>
      api<{ data: Taxonomy[] }>(`/api/document-taxonomy/${kind}`).then((response) => response.data),
  });
}

function LegalPill({ status }: { status: LegalStatus }) {
  return (
    <span
      className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${legalStyles[status]}`}
    >
      {legalLabels[status]}
    </span>
  );
}

function meetingExcerpt(meeting: MeetingNode, term: string, includeTranscript: boolean) {
  const needle = term.toLocaleLowerCase();
  for (const [label, content] of [
    ['สรุป', meeting.summary],
    ['มติ', meeting.decisions],
    ['บันทึก', meeting.notes],
  ] as const) {
    const index = content?.toLocaleLowerCase().indexOf(needle) ?? -1;
    if (content && index >= 0)
      return `${label}: …${content.slice(Math.max(0, index - 45), index + term.length + 95)}…`;
  }
  if (meeting.title.toLocaleLowerCase().includes(needle)) return 'พบในชื่อการประชุม';
  return includeTranscript ? 'พบในไฟล์แนบหรือ Transcript' : 'พบในไฟล์แนบหรือชื่อไฟล์ประชุม';
}

function SearchFilters({
  categories,
  agencies,
  labels,
  categoryId,
  setCategoryId,
  agencyId,
  setAgencyId,
  labelIds,
  setLabelIds,
  status,
  setStatus,
  legalStatus,
  setLegalStatus,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  relationshipType,
  setRelationshipType,
  relationshipDirection,
  setRelationshipDirection,
  clear,
}: {
  categories: Taxonomy[];
  agencies: Taxonomy[];
  labels: Taxonomy[];
  categoryId: string;
  setCategoryId: (value: string) => void;
  agencyId: string;
  setAgencyId: (value: string) => void;
  labelIds: string[];
  setLabelIds: (value: string[]) => void;
  status: string;
  setStatus: (value: string) => void;
  legalStatus: string;
  setLegalStatus: (value: string) => void;
  dateFrom: string;
  setDateFrom: (value: string) => void;
  dateTo: string;
  setDateTo: (value: string) => void;
  relationshipType: string;
  setRelationshipType: (value: string) => void;
  relationshipDirection: string;
  setRelationshipDirection: (value: string) => void;
  clear: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-xs text-slate-600">
          ประเภทเอกสาร
          <select
            className="field mt-1"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">ทุกประเภท</option>
            {categories.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          หน่วยงาน
          <select
            className="field mt-1"
            value={agencyId}
            onChange={(e) => setAgencyId(e.target.value)}
          >
            <option value="">ทุกหน่วยงาน</option>
            {agencies.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          สถานะเอกสาร
          <select className="field mt-1" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">ทุกสถานะ</option>
            {Object.entries(statusLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          สถานะทางกฎหมาย
          <select
            className="field mt-1"
            value={legalStatus}
            onChange={(e) => setLegalStatus(e.target.value)}
          >
            <option value="">ทุกสถานะ</option>
            {Object.entries(legalLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-xs text-slate-600">
          วันที่เอกสารตั้งแต่
          <input
            className="field mt-1"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </label>
        <label className="text-xs text-slate-600">
          ถึงวันที่
          <input
            className="field mt-1"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </label>
        <label className="text-xs text-slate-600">
          ชนิดความสัมพันธ์
          <select
            className="field mt-1"
            value={relationshipType}
            onChange={(e) => setRelationshipType(e.target.value)}
          >
            <option value="">ทุกชนิด</option>
            {Object.entries(relationshipLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          ตำแหน่งในสายเอกสาร
          <select
            className="field mt-1"
            value={relationshipDirection}
            onChange={(e) => setRelationshipDirection(e.target.value)}
          >
            <option value="">ทั้งหมด</option>
            <option value="incoming">มีฉบับใหม่มากระทบ</option>
            <option value="outgoing">เชื่อมไปยังฉบับเดิม</option>
            <option value="unlinked">ยังไม่เชื่อมโยง</option>
          </select>
        </label>
      </div>
      <div>
        <p className="mb-1 text-xs text-slate-600">
          Label (เลือกหลายรายการเพื่อค้นหาเอกสารที่มีครบทุก Label)
        </p>
        <div className="flex flex-wrap gap-1.5">
          {labels.map((item) => (
            <button
              type="button"
              key={item.id}
              onClick={() =>
                setLabelIds(
                  labelIds.includes(item.id)
                    ? labelIds.filter((id) => id !== item.id)
                    : [...labelIds, item.id],
                )
              }
              className={`rounded-full border px-2.5 py-1 text-xs ${labelIds.includes(item.id) ? 'border-teal-600 bg-teal-50 font-semibold text-teal-800' : 'border-slate-200 bg-white text-slate-600'}`}
            >
              {labelIds.includes(item.id) ? '✓ ' : ''}
              {item.name}
            </button>
          ))}
          {labels.length === 0 && <span className="text-xs text-slate-400">ยังไม่มี Label</span>}
        </div>
      </div>
      <div className="flex justify-end">
        <button type="button" className="text-sm font-semibold text-[#8bd8ff]" onClick={clear}>
          ล้างตัวกรองทั้งหมด
        </button>
      </div>
    </div>
  );
}

function RelationshipTree({
  graph,
  showProposed,
  onSelect,
}: {
  graph: GraphResponse;
  showProposed: boolean;
  onSelect: (id: string) => void;
}) {
  const nodes = new Map(graph.nodes.map((node) => [node.id, node]));
  const edges = graph.edges.filter((edge) => edge.status === 'confirmed' || showProposed);
  const renderBranch = (
    id: string,
    direction: 'incoming' | 'outgoing',
    visited: Set<string>,
    depth: number,
  ): ReactNode => {
    if (depth > 8) return <p className="ml-6 text-xs text-slate-400">แสดงถึงระดับสูงสุดแล้ว</p>;
    const related = edges.filter((edge) =>
      direction === 'incoming' ? edge.target.id === id : edge.source.id === id,
    );
    if (!related.length) return null;
    return (
      <div className="ml-4 space-y-2 border-l-2 border-slate-200 pl-3">
        {related.map((edge) => {
          const nextId = direction === 'incoming' ? edge.source.id : edge.target.id;
          const node = nodes.get(nextId);
          if (!node) return null;
          const repeated = visited.has(nextId);
          const nextVisited = new Set(visited);
          nextVisited.add(nextId);
          return (
            <div key={`${direction}-${edge.id}`}>
              <button
                type="button"
                onClick={() => onSelect(node.id)}
                className={`w-full rounded-xl border border-[#466985] p-3 text-left text-[#eaf4ff] transition hover:border-sky-400 ${edge.status === 'proposed' ? 'border-dashed bg-[#2a3159]' : 'bg-[#1e344e]'}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-[#79d3ff]">
                    {relationshipLabels[edge.relationshipType]}
                  </span>
                  {edge.status === 'proposed' && (
                    <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] text-violet-800">
                      รอตรวจสอบ
                    </span>
                  )}
                  <LegalPill status={node.legalStatus} />
                </div>
                <p className="mt-1 font-semibold">{node.documentNumber ?? node.title}</p>
                {node.documentNumber && <p className="text-sm text-slate-600">{node.title}</p>}
                <p className="mt-1 text-xs text-slate-500">
                  {node.documentDate ?? 'ไม่ระบุวันที่'}
                  {edge.effectiveDate ? ` · มีผล ${edge.effectiveDate}` : ''}
                </p>
                {edge.scopeText && (
                  <p className="mt-2 rounded-lg bg-[#453b28] p-2 text-xs text-[#ffe3a2]">
                    ขอบเขต: {edge.scopeText}
                  </p>
                )}
                {edge.evidenceText && (
                  <p className="mt-2 line-clamp-2 text-xs text-slate-500">
                    หลักฐาน{edge.evidencePage ? ` หน้า ${edge.evidencePage}` : ''}:{' '}
                    {edge.evidenceText}
                  </p>
                )}
              </button>
              {!repeated && renderBranch(nextId, direction, nextVisited, depth + 1)}
            </div>
          );
        })}
      </div>
    );
  };
  const root = nodes.get(graph.rootId);
  if (!root) return <p className="text-sm text-slate-500">ไม่พบข้อมูลสายเอกสาร</p>;
  return (
    <div className="space-y-4">
      <section>
        <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
          ฉบับใหม่หรือเอกสารที่มีผลต่อฉบับนี้
        </h3>
        {renderBranch(root.id, 'incoming', new Set([root.id]), 0) ?? (
          <p className="rounded-xl bg-[#203650] p-3 text-sm text-slate-500">
            ยังไม่มีฉบับใหม่ที่เชื่อมโยง
          </p>
        )}
      </section>
      <button
        type="button"
        onClick={() => onSelect(root.id)}
        className="w-full rounded-2xl border-2 border-sky-400 bg-[#254766] p-4 text-left text-[#eaf4ff] shadow-sm"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-bold text-[#8bd8ff]">เอกสารที่เลือก</span>
          <LegalPill status={root.legalStatus} />
        </div>
        <p className="mt-1 text-lg font-bold">{root.documentNumber ?? root.title}</p>
        {root.documentNumber && <p className="text-sm text-slate-700">{root.title}</p>}
        <p className="mt-1 text-xs text-slate-500">{root.documentDate ?? 'ไม่ระบุวันที่'}</p>
      </button>
      <section>
        <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
          ฉบับเดิมหรือเอกสารที่ฉบับนี้อ้างถึง
        </h3>
        {renderBranch(root.id, 'outgoing', new Set([root.id]), 0) ?? (
          <p className="rounded-xl bg-[#203650] p-3 text-sm text-slate-500">
            ยังไม่มีฉบับเดิมที่เชื่อมโยง
          </p>
        )}
      </section>
    </div>
  );
}

export function DocumentSearchPage() {
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query.trim());
  const [scope, setScope] = useState<'all' | 'documents' | 'meetings'>('all');
  const [includeTranscript, setIncludeTranscript] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [agencyId, setAgencyId] = useState('');
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [status, setStatus] = useState('');
  const [legalStatus, setLegalStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [relationshipType, setRelationshipType] = useState('');
  const [relationshipDirection, setRelationshipDirection] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const detailRef = useRef<HTMLElement>(null);
  const matchedFileRef = useRef<HTMLDetailsElement>(null);
  const [view, setView] = useState<'list' | 'tree'>('list');
  const [showProposed, setShowProposed] = useState(false);
  const [page, setPage] = useState({ key: '', offset: 0 });
  const limit = 30;
  const categories = useTaxonomy('categories');
  const agencies = useTaxonomy('agencies');
  const labels = useTaxonomy('labels');
  const filterParams = useMemo(() => {
    const p = new URLSearchParams();
    if (deferredQuery) p.set('query', deferredQuery);
    if (categoryId) p.set('categoryId', categoryId);
    if (agencyId) p.set('agencyId', agencyId);
    if (labelIds.length) p.set('labelIds', labelIds.join(','));
    if (status) p.set('status', status);
    if (legalStatus) p.set('legalStatus', legalStatus);
    if (dateFrom) p.set('dateFrom', dateFrom);
    if (dateTo) p.set('dateTo', dateTo);
    if (relationshipType) p.set('relationshipType', relationshipType);
    if (relationshipDirection) p.set('relationshipDirection', relationshipDirection);
    return p.toString();
  }, [
    deferredQuery,
    categoryId,
    agencyId,
    labelIds,
    status,
    legalStatus,
    dateFrom,
    dateTo,
    relationshipType,
    relationshipDirection,
  ]);
  const offset = page.key === filterParams ? page.offset : 0;
  const params = useMemo(() => {
    const p = new URLSearchParams(filterParams);
    p.set('limit', String(limit));
    p.set('offset', String(offset));
    return p.toString();
  }, [filterParams, offset]);
  const results = useQuery({
    queryKey: ['document-search', params],
    queryFn: () => api<SearchResponse>(`/api/documents?${params}`),
    enabled: scope !== 'meetings',
  });
  const meetingResults = useQuery({
    queryKey: ['workspace-meeting-search', deferredQuery, includeTranscript],
    queryFn: () =>
      api<{ data: Array<MeetingNode & { topicTitle: string }> }>(
        `/api/meeting-search?query=${encodeURIComponent(deferredQuery)}&includeTranscript=${includeTranscript}&limit=100`,
      ).then((response) => response.data),
    enabled: scope !== 'documents' && deferredQuery.length >= 2,
  });
  const activeSelectedId = scope === 'meetings' ? null : (
    selectedId && results.data?.data.some((document) => document.id === selectedId)
      ? selectedId
      : (results.data?.data?.[0]?.id ?? null)
  );
  const activeMatch = results.data?.data.find((document) => document.id === activeSelectedId)?.match;
  const detail = useQuery({
    queryKey: ['library-document', activeSelectedId],
    enabled: Boolean(activeSelectedId),
    queryFn: () =>
      api<{ data: SearchDocument }>(`/api/documents/${activeSelectedId}`).then((r) => r.data),
  });
  useEffect(() => {
    if (selectedId && detail.data?.id === selectedId) (matchedFileRef.current ?? detailRef.current)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectedId, detail.data?.id, activeMatch?.fileId]);
  const graph = useQuery({
    queryKey: ['document-relationship-graph', activeSelectedId],
    enabled: Boolean(activeSelectedId) && view === 'tree',
    queryFn: () =>
      api<{ data: GraphResponse }>(`/api/documents/${activeSelectedId}/relationship-graph`).then(
        (r) => r.data,
      ),
  });
  const clearDocumentFilters = () => {
    setCategoryId('');
    setAgencyId('');
    setLabelIds([]);
    setStatus('');
    setLegalStatus('');
    setDateFrom('');
    setDateTo('');
    setRelationshipType('');
    setRelationshipDirection('');
  };
  const activeFilters = [
    categoryId,
    agencyId,
    status,
    legalStatus,
    dateFrom,
    dateTo,
    relationshipType,
    relationshipDirection,
    ...labelIds,
  ].filter(Boolean).length;
  return (
    <Layout title="ค้นหา" wide>
      <div className="space-y-4">
        <section className="workspace-search-hero space-y-3">
          <div>
            <p className="workspace-eyebrow">SEARCH WORKSPACE</p>
            <h2 className="text-2xl font-bold">ค้นหาเอกสารและการประชุม</h2>
            <p className="text-sm text-slate-500">
              เลือกแหล่งข้อมูล แล้วค้นชื่อ สรุป มติ หรือข้อความที่อ่านจากไฟล์
            </p>
          </div>
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-3 text-[#69cfff]">⌕</span>
            <input
              className="field workspace-search-input pl-10 text-base"
              aria-label="ค้นหาเอกสารและการประชุม"
              placeholder="ค้นชื่อ เลขที่หนังสือ หัวข้อประชุม หรือข้อความ…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {scope === 'documents' && (
            <details
              className="workspace-search-filters rounded-xl border p-3"
              open={activeFilters > 0 ? true : undefined}
            >
              <summary className="cursor-pointer font-semibold">
                ตัวกรองคลังเอกสาร
                {activeFilters > 0 && (
                  <span className="ml-2 rounded-full bg-blue-900 px-2 py-0.5 text-xs text-blue-100">
                    {activeFilters}
                  </span>
                )}
              </summary>
              <div className="mt-3">
                <SearchFilters
                  categories={categories.data ?? []}
                  agencies={agencies.data ?? []}
                  labels={labels.data ?? []}
                  {...{
                    categoryId,
                    setCategoryId,
                    agencyId,
                    setAgencyId,
                    labelIds,
                    setLabelIds,
                    status,
                    setStatus,
                    legalStatus,
                    setLegalStatus,
                    dateFrom,
                    setDateFrom,
                    dateTo,
                    setDateTo,
                    relationshipType,
                    setRelationshipType,
                    relationshipDirection,
                    setRelationshipDirection,
                    clear: clearDocumentFilters,
                  }}
                />
              </div>
            </details>
          )}
          {scope !== 'documents' && (
            <label className="flex min-h-11 items-center gap-3 text-sm text-[#c7dff0]">
              <input
                type="checkbox"
                checked={includeTranscript}
                onChange={(event) => setIncludeTranscript(event.target.checked)}
                className="h-5 w-5 accent-sky-400"
              />
              ค้นใน Transcript เพิ่มเติม
            </label>
          )}
        </section>
        <div className="workspace-search-layout">
          <aside className="workspace-search-rail" aria-label="เลือกแหล่งค้นหา">
            <h3 className="font-semibold">ค้นใน</h3>
            {(
              [
                ['all', 'ทั้งหมด', 'คลังเอกสาร + การประชุม'],
                ['documents', 'คลังเอกสาร', 'OCR และสายเอกสาร'],
                ['meetings', 'การประชุม', 'สรุป มติ และไฟล์แนบ'],
              ] as const
            ).map(([value, label, description]) => (
              <button
                key={value}
                type="button"
                aria-pressed={scope === value}
                className={`workspace-search-scope ${scope === value ? 'is-active' : ''}`}
                onClick={() => {
                  setScope(value);
                  setSelectedId(null);
                  if (value !== 'documents') clearDocumentFilters();
                }}
              >
                <span className="font-semibold">{label}</span>
                <span className="text-xs">{description}</span>
              </button>
            ))}
          </aside>
          <div className="min-w-0 space-y-4">
            <div
              className={`grid gap-4 ${scope === 'meetings' ? '' : 'xl:grid-cols-[minmax(320px,0.8fr)_minmax(0,1.2fr)]'}`}
            >
              <div className="min-w-0 space-y-4">
                {scope !== 'meetings' && (
                  <section className="card p-0">
                    <div className="flex items-center justify-between border-b p-4">
                      <div>
                        <h2 className="font-bold">ผลจากคลังเอกสาร</h2>
                        <p className="text-xs text-slate-500">
                          พบ {results.data?.meta?.total ?? 0} รายการ
                        </p>
                      </div>
                      {results.isFetching && (
                        <span className="text-xs text-[#8bd8ff]">กำลังค้นหา…</span>
                      )}
                    </div>
                    {results.error && (
                      <div className="p-4">
                        <ErrorNotice message={results.error.message} />
                      </div>
                    )}
                    <div className="max-h-[68vh] divide-y overflow-y-auto">
                      {results.data?.data?.map((doc) => (
                        <button
                          type="button"
                          key={doc.id}
                          onClick={() => { setSelectedId(doc.id); setView('list'); }}
                          className={`w-full p-4 text-left transition hover:bg-[#213b58] ${activeSelectedId === doc.id ? 'bg-[#254766] ring-1 ring-inset ring-sky-400' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-semibold">{doc.documentNumber ?? doc.title}</p>
                              {doc.documentNumber && (
                                <p className="truncate text-sm text-slate-700">{doc.title}</p>
                              )}
                              <p className="mt-1 text-xs text-slate-500">
                                {doc.documentDate ?? 'ไม่ระบุวันที่'} ·{' '}
                                {doc.agency?.name ?? 'ไม่ระบุหน่วยงาน'}
                              </p>
                            </div>
                            <LegalPill status={doc.legalStatus} />
                          </div>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {doc.category && (
                              <span
                                className="rounded-full px-2 py-0.5 text-xs"
                                style={{
                                  backgroundColor: `${doc.category.color}18`,
                                  color: doc.category.color,
                                }}
                              >
                                {doc.category.name}
                              </span>
                            )}
                            {doc.labels.map((label) => (
                              <span
                                key={label.id}
                                className="rounded-full border px-2 py-0.5 text-xs"
                              >
                                {label.name}
                              </span>
                            ))}
                          </div>
                          {doc.match && (
                            <div className="mt-2 rounded-lg bg-[#3d3628] p-2 text-xs text-[#ffedc8]">
                              <p className="mb-1 font-semibold text-[#ffd17f]">
                                พบใน{' '}
                                {doc.match.source === 'metadata'
                                  ? 'ชื่อหรือเลขที่เอกสาร'
                                  : (doc.match.fileName ?? 'เอกสารต้นฉบับ')}
                                {doc.match.page ? ` · หน้า ${doc.match.page}` : ''}
                              </p>
                              <p>{doc.match.snippet}</p>
                            </div>
                          )}
                        </button>
                      ))}
                      {results.data?.data?.length === 0 && (
                        <p className="p-10 text-center text-sm text-slate-500">
                          ไม่พบเอกสารตามเงื่อนไข
                        </p>
                      )}
                    </div>
                    {(results.data?.meta?.total ?? 0) > limit && (
                      <div className="flex items-center justify-between border-t p-3">
                        <button
                          className="btn-secondary"
                          disabled={offset === 0}
                          onClick={() =>
                            setPage({ key: filterParams, offset: Math.max(0, offset - limit) })
                          }
                        >
                          ก่อนหน้า
                        </button>
                        <span className="text-xs text-slate-500">
                          {offset + 1}–{Math.min(offset + limit, results.data?.meta?.total ?? 0)}
                        </span>
                        <button
                          className="btn-secondary"
                          disabled={offset + limit >= (results.data?.meta?.total ?? 0)}
                          onClick={() => setPage({ key: filterParams, offset: offset + limit })}
                        >
                          ถัดไป
                        </button>
                      </div>
                    )}
                  </section>
                )}
                {scope !== 'documents' && (
                  <section className="card p-0">
                    <div className="border-b p-4">
                      <h2 className="font-bold">ผลจากการประชุม</h2>
                      <p className="text-xs text-slate-500">
                        {deferredQuery.length < 2
                          ? 'พิมพ์อย่างน้อย 2 ตัวอักษรเพื่อค้นหา'
                          : `พบ ${meetingResults.data?.length ?? 0} รายการ`}
                      </p>
                    </div>
                    {meetingResults.error && (
                      <div className="p-4">
                        <ErrorNotice message={meetingResults.error.message} />
                      </div>
                    )}
                    {meetingResults.isFetching && (
                      <p className="p-4 text-sm text-slate-500">กำลังค้นการประชุม…</p>
                    )}
                    {deferredQuery.length >= 2 &&
                      !meetingResults.isFetching &&
                      meetingResults.data?.length === 0 && (
                        <p className="p-6 text-sm text-slate-500">ไม่พบการประชุมตามคำค้น</p>
                      )}
                    <div className="max-h-[68vh] divide-y overflow-y-auto">
                      {meetingResults.data?.map((meeting) => (
                        <Link
                          key={meeting.id}
                          to={`/meeting-topics/${meeting.topicId}?${new URLSearchParams({ meeting: meeting.id, find: deferredQuery })}`}
                          className="workspace-search-meeting-result block p-4 transition"
                        >
                          <p className="text-xs text-[#79d3ff]">
                            ⌘ การประชุม · {meeting.topicTitle}
                            {meeting.heldAt ? ` · ${meeting.heldAt.slice(0, 10)}` : ''}
                          </p>
                          <h3 className="mt-1 font-semibold">{meeting.title}</h3>
                          <p className="mt-1 line-clamp-2 text-sm text-slate-500">
                            {meetingExcerpt(meeting, deferredQuery, includeTranscript)}
                          </p>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
              </div>
              {scope !== 'meetings' && (
                <section ref={detailRef} className="card min-h-[420px] scroll-mt-24">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex rounded-xl bg-[#101d30] p-1">
                      <button
                        type="button"
                        className={`rounded-lg px-3 py-2 text-sm font-semibold ${view === 'list' ? 'bg-[#254766] text-[#8bd8ff] shadow-sm' : 'text-slate-500'}`}
                        onClick={() => setView('list')}
                      >
                        รายละเอียด
                      </button>
                      <button
                        type="button"
                        className={`rounded-lg px-3 py-2 text-sm font-semibold ${view === 'tree' ? 'bg-[#254766] text-[#8bd8ff] shadow-sm' : 'text-slate-500'}`}
                        onClick={() => setView('tree')}
                      >
                        สายเอกสาร Tree
                      </button>
                    </div>
                    {view === 'tree' && (
                      <label className="flex items-center gap-2 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          checked={showProposed}
                          onChange={(e) => setShowProposed(e.target.checked)}
                        />
                        แสดงรายการรอตรวจสอบ
                      </label>
                    )}
                  </div>
                  {!activeSelectedId ? (
                    <p className="py-20 text-center text-sm text-slate-500">
                      เลือกเอกสารจากผลการค้นหา
                    </p>
                  ) : view === 'tree' ? (
                    graph.isLoading ? (
                      <p className="py-20 text-center text-sm text-slate-500">
                        กำลังสร้างสายเอกสาร…
                      </p>
                    ) : graph.error ? (
                      <ErrorNotice message={graph.error.message} />
                    ) : graph.data ? (
                      <RelationshipTree
                        graph={graph.data}
                        showProposed={showProposed}
                        onSelect={setSelectedId}
                      />
                    ) : null
                  ) : detail.isLoading ? (
                    <p className="py-20 text-center text-sm text-slate-500">กำลังโหลดรายละเอียด…</p>
                  ) : detail.error ? (
                    <ErrorNotice message={detail.error.message} />
                  ) : (
                    detail.data && (
                      <div className="space-y-4">
                        <div>
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <p className="text-xs text-slate-500">
                                {detail.data.documentNumber ?? 'ไม่ระบุเลขที่เอกสาร'}
                              </p>
                              <h2 className="text-xl font-bold">{detail.data.title}</h2>
                            </div>
                            <LegalPill status={detail.data.legalStatus} />
                          </div>
                          <dl className="mt-4 grid gap-3 rounded-xl border border-[#355574] bg-[#101d30] p-4 text-sm sm:grid-cols-2">
                            <div>
                              <dt className="text-xs text-slate-500">วันที่เอกสาร</dt>
                              <dd>{detail.data.documentDate ?? 'ไม่ระบุ'}</dd>
                            </div>
                            <div>
                              <dt className="text-xs text-slate-500">หน่วยงาน</dt>
                              <dd>{detail.data.agency?.name ?? 'ไม่ระบุ'}</dd>
                            </div>
                            <div>
                              <dt className="text-xs text-slate-500">ประเภท</dt>
                              <dd>{detail.data.category?.name ?? 'ไม่ระบุ'}</dd>
                            </div>
                            <div>
                              <dt className="text-xs text-slate-500">สถานะการประมวลผล</dt>
                              <dd className="mt-1">
                                <StatusPill status={detail.data.status} />
                              </dd>
                            </div>
                          </dl>
                        </div>
                        {detail.data.summaryText && (
                          <div className="rounded-xl border border-[#466985] bg-[#203650] p-4">
                            <h3 className="font-semibold">สรุปเอกสาร</h3>
                            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                              {detail.data.summaryText}
                            </p>
                          </div>
                        )}
                        <div className="space-y-3">
                          <h3 className="font-semibold">ข้อความที่ค้นหาได้จากไฟล์</h3>
                          <details key={`${detail.data.id}-original-${deferredQuery}`} className="rounded-xl border border-[#355574] p-3" open={Boolean(deferredQuery && (activeMatch?.source === 'original' || detail.data.ocrText?.toLocaleLowerCase('th').includes(deferredQuery.toLocaleLowerCase('th')))) || undefined}>
                            <summary className="cursor-pointer text-sm font-semibold">ไฟล์ต้นฉบับ · {detail.data.file.name}</summary>
                            <div className="mt-3"><SearchableTextPanel key={`${detail.data.id}-${deferredQuery}`} title="ไฟล์ต้นฉบับ" text={detail.data.ocrText ?? ''} initialQuery={deferredQuery} /></div>
                          </details>
                          {(detail.data.files ?? []).map((file) => (
                            <details ref={activeMatch?.source === 'attachment' && activeMatch.fileId === file.id ? matchedFileRef : undefined} key={`${file.id}-${deferredQuery}`} className="rounded-xl border border-[#355574] p-3 scroll-mt-24" open={Boolean(deferredQuery && ((activeMatch?.source === 'attachment' && activeMatch.fileId === file.id) || file.ocrText?.toLocaleLowerCase('th').includes(deferredQuery.toLocaleLowerCase('th')))) || undefined}>
                              <summary className="cursor-pointer text-sm font-semibold">ไฟล์แนบ · {file.displayName}</summary>
                              <div className="mt-3"><SearchableTextPanel key={`${file.id}-${deferredQuery}`} title={`ไฟล์ ${file.displayName}`} text={file.ocrText ?? ''} initialQuery={deferredQuery} /></div>
                            </details>
                          ))}
                          {activeMatch?.source === 'metadata' && <p className="text-xs text-slate-500">คำค้นพบในชื่อหรือเลขที่เอกสาร</p>}
                        </div>
                        <div className="grid gap-2 sm:grid-cols-2">
                          <a
                            className="btn-primary text-center"
                            href={apiUrl(
                              `/api/files/${detail.data.storedFileId}/content?inline=true`,
                            )}
                            target="_blank"
                            rel="noreferrer"
                          >
                            เปิดไฟล์ต้นฉบับ
                          </a>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => setView('tree')}
                          >
                            ดูสายเอกสารทั้งหมด
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </section>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
